import {useEffect, useState, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import {Confirm, Loading, Notify} from 'notiflix';
import {
  Card,
  CircularProgress,
  Grid,
  Divider,
  Icon,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Box,
  Pagination,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import DataTable from 'examples/Tables/DataTable';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {errorLogsApi} from 'api/errorLogsApi';
import useDebounce from 'hooks/useDebounce';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const columns = [
  {Header: 'Time', accessor: 'time', align: 'left'},
  {Header: 'Method', accessor: 'method', align: 'center'},
  {Header: 'Path', accessor: 'path', align: 'left'},
  {Header: 'Status', accessor: 'statusCode', align: 'center'},
  {Header: 'Error Type', accessor: 'errorType', align: 'left'},
  {Header: 'Message', accessor: 'message', align: 'left'},
  {Header: 'Actions', accessor: 'actions', align: 'center'},
];

const statusBadgeColor = statusCode => {
  if (!statusCode) return 'secondary';
  if (statusCode >= 500) return 'error';
  if (statusCode >= 400) return 'warning';
  return 'success';
};

const AUTO_REFRESH_INTERVAL = 45000;

const ErrorLogs = () => {
  const {user} = useSelector(state => state.auth);
  const navigate = useNavigate();
  const canRead = usePermission('error-logs', 'read');
  const canDelete = usePermission('error-logs', 'delete');
  const canPurge = usePermission('error-logs', 'purge');

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusCode, setStatusCode] = useState('');
  const [method, setMethod] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 800);
  const debouncedStatusCode = useDebounce(statusCode, 800);

  const fetchLogs = useCallback(
    async ({silent = false} = {}) => {
      if (!canRead) {
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      setError(null);
      try {
        const params = {page, limit};
        if (debouncedStatusCode) params.statusCode = debouncedStatusCode;
        if (method) params.method = method;
        if (from && !isNaN(new Date(from).getTime())) params.from = new Date(from).toISOString();
        if (to && !isNaN(new Date(to).getTime())) params.to = new Date(to).toISOString();
        if (debouncedSearch) params.search = debouncedSearch;

        const data = await errorLogsApi.fetchLogs(user?.accessToken, params);
        setLogs(data?.logs || []);
        setTotal(data?.total || 0);
        setTotalPages(data?.totalPages || 1);
      } catch (err) {
        console.error('Error fetching error logs:', err);
        setError('Failed to load error logs');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [page, limit, debouncedStatusCode, method, from, to, debouncedSearch, user?.accessToken, canRead],
  );

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedStatusCode, method, from, to, debouncedSearch, canRead]);

  useEffect(() => {
    const interval = setInterval(() => fetchLogs({silent: true}), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLogs]);

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedStatusCode, method, from, to, debouncedSearch]);

  const handleDelete = (id, message) => {
    Confirm.show(
      'Delete Error Log?',
      `Are you sure you want to delete this log entry?\n\n"${(message || '').slice(0, 120)}"`,
      'Delete',
      'Cancel',
      async () => {
        try {
          Loading.circle('Deleting...');
          await errorLogsApi.deleteLog(user?.accessToken, id);
          Loading.remove();
          Notify.success('Log deleted');
          setLogs(prev => prev.filter(l => l.id !== id));
          fetchLogs({silent: true});
        } catch (err) {
          Loading.remove();
          Notify.failure(err?.response?.data?.message || 'Failed to delete log');
        }
      },
      () => {},
    );
  };

  const handleClearAll = () => {
    Confirm.show(
      'Clear All Error Logs?',
      'This will permanently delete ALL error logs. This action cannot be undone.',
      'Clear All',
      'Cancel',
      async () => {
        try {
          Loading.circle('Clearing logs...');
          await errorLogsApi.clearAllLogs(user?.accessToken);
          Loading.remove();
          Notify.success('All error logs cleared');
          setPage(1);
          fetchLogs();
        } catch (err) {
          Loading.remove();
          Notify.failure(err?.response?.data?.message || 'Failed to clear logs');
        }
      },
      () => {},
    );
  };

  const getRow = row => ({
    time: (
      <Tooltip title={moment(row.createdAt).format('DD MMM YYYY, HH:mm:ss')}>
        <MDTypography variant="caption">{moment(row.createdAt).fromNow()}</MDTypography>
      </Tooltip>
    ),
    method: row.method ? (
      <MDBadge badgeContent={row.method} color="info" variant="gradient" size="sm" />
    ) : (
      <MDTypography variant="caption">—</MDTypography>
    ),
    path: (
      <Tooltip title={row.path || ''}>
        <MDTypography
          variant="caption"
          sx={{
            display: 'block',
            maxWidth: 260,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {row.path}
        </MDTypography>
      </Tooltip>
    ),
    statusCode: row.statusCode ? (
      <MDBadge
        badgeContent={row.statusCode}
        max={999}
        color={statusBadgeColor(row.statusCode)}
        variant="gradient"
        size="sm"
      />
    ) : (
      <MDTypography variant="caption">—</MDTypography>
    ),
    errorType: <MDTypography variant="caption">{row.errorType || '—'}</MDTypography>,
    message: (
      <Tooltip title={row.message || ''}>
        <MDTypography
          variant="caption"
          sx={{
            display: 'block',
            maxWidth: 320,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {row.message}
        </MDTypography>
      </Tooltip>
    ),
    actions: (
      <MDBox display="flex" gap={1} justifyContent="center">
        <Tooltip title="View Details">
          <IconButton size="small" onClick={() => navigate(`/error-logs/${row.id}`)}>
            <Icon fontSize="small">visibility</Icon>
          </IconButton>
        </Tooltip>
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(row.id, row.message)}>
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        )}
      </MDBox>
    ),
  });


  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to Error Logs. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} sx={{display: 'flex'}}>
            <Divider orientation="vertical" sx={{ml: -2, mr: 1}} />
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}>
                  <MDTypography variant="h6" color="white">
                    Error Logs ({total})
                  </MDTypography>
                  <MDBox display="flex" gap={1}>
                    <MDButton variant="outlined" color="white" onClick={() => fetchLogs()}>
                      <Icon sx={{mr: 0.5}}>refresh</Icon>
                      Refresh
                    </MDButton>
                    {canPurge && (
                      <MDButton variant="contained" color="error" onClick={handleClearAll}>
                        Clear All Logs
                      </MDButton>
                    )}
                  </MDBox>
                </MDBox>

                <Grid container spacing={2} p={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <MDInput
                      fullWidth
                      label="Search message"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MDInput
                      fullWidth
                      label="Status Code"
                      placeholder="e.g. 404"
                      value={statusCode}
                      onChange={e => setStatusCode(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Method</InputLabel>
                      <Select value={method} onChange={e => setMethod(e.target.value)} label="Method">
                        <MenuItem value="">All Methods</MenuItem>
                        {METHOD_OPTIONS.map(m => (
                          <MenuItem key={m} value={m}>
                            {m}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MDInput
                      fullWidth
                      type="date"
                      label="From"
                      InputLabelProps={{shrink: true}}
                      value={from}
                      onChange={e => setFrom(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <MDInput
                      fullWidth
                      type="date"
                      label="To"
                      InputLabelProps={{shrink: true}}
                      value={to}
                      onChange={e => setTo(e.target.value)}
                    />
                  </Grid>
                </Grid>

                {loading && (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={6}>
                    <Box textAlign="center">
                      <CircularProgress size="2.5rem" color="inherit" />
                      <MDTypography variant="button" color="text" display="block" mt={1}>
                        Loading error logs...
                      </MDTypography>
                    </Box>
                  </MDBox>
                )}

                {!loading && error && (
                  <MDBox textAlign="center" py={6} px={3}>
                    <Icon sx={{fontSize: 48, color: 'error.main', mb: 2}}>error_outline</Icon>
                    <MDTypography variant="h6" mb={2}>
                      {error}
                    </MDTypography>
                    <MDButton variant="contained" color="info" onClick={() => fetchLogs()}>
                      Retry
                    </MDButton>
                  </MDBox>
                )}

                {!loading && !error && logs.length === 0 && (
                  <MDBox textAlign="center" py={6}>
                    <Icon sx={{fontSize: 64, color: 'success.main', mb: 2}}>check_circle</Icon>
                    <MDTypography variant="h6" mb={1}>
                      No errors logged — looking good!
                    </MDTypography>
                  </MDBox>
                )}

                {!loading && !error && logs.length > 0 && (
                  <>
                    <MDBox pt={3}>
                      <DataTable
                        key={logs.map(l => l.id).join('|')}
                        table={{
                          columns,
                          rows: logs.map(row => getRow(row)),
                        }}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                        hoverEffect
                      />
                    </MDBox>
                    <MDBox display="flex" justifyContent="flex-end" py={3} px={2}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="info"
                        shape="rounded"
                      />
                    </MDBox>
                  </>
                )}
              </Card>
            </Grid>
            <Divider orientation="vertical" sx={{mx: 0}} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default ErrorLogs;
